import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { usePageContext } from "../hooks/usePageContext";
import TeacherBubble from "./teacher/TeacherBubble";
import AvatarButton from "./teacher/AvatarButton";

/**
 * Floating EGNS teacher avatar ("Dr. Nesma"). Clicking triggers British-accent
 * narration of whatever page is currently open. Audio is pre-generated and
 * cached server-side (OpenAI TTS nova @ 0.88 speed).
 */
export default function TeacherAvatar() {
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const location = useLocation();
  const pageContext = usePageContext();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");
  const [script, setScript] = useState("");

  const audioRef = useRef(null);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  }, []);

  // Stop playing + reset panel when the route changes.
  useEffect(() => {
    stop();
  }, [pageContext.page, pageContext.chapter_id, pageContext.kind, stop]);

  const fetchAndPlay = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/tts/teacher", pageContext);
      setScript(res.data.script);
      if (audioRef.current) {
        audioRef.current.src = res.data.audio;
        await audioRef.current.play();
        setPlaying(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't generate narration";
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  }, [pageContext]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else if (audioRef.current.src && !audioRef.current.ended) {
      audioRef.current.play();
      setPlaying(true);
    } else {
      fetchAndPlay();
    }
  }, [playing, fetchAndPlay]);

  const handleAvatarClick = () => {
    if (!open) {
      setOpen(true);
      fetchAndPlay();
    } else {
      togglePlay();
    }
  };

  const handleClose = () => { stop(); setOpen(false); };

  // Hide on admin pages — the teacher is for students.
  const p = location.pathname;
  if (p.startsWith("/admin")) return null;
  if (user && user.status === "pending" && !p.startsWith("/pending")) return null;

  return (
    <div
      className={`fixed bottom-20 ${isRTL ? "left-5" : "right-5"} z-[55] flex flex-col items-end gap-3`}
      data-testid="teacher-avatar-root"
    >
      {open && (
        <TeacherBubble
          isRTL={isRTL}
          loading={loading}
          playing={playing}
          error={error}
          script={script}
          onClose={handleClose}
          onTogglePlay={togglePlay}
        />
      )}

      <AvatarButton
        user={user}
        loading={loading}
        playing={playing}
        onClick={handleAvatarClick}
      />

      <audio
        ref={audioRef}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        preload="none"
      />

      <style>{`
        @keyframes speak-bar {
          0%, 100% { height: 4px; }
          50% { height: 14px; }
        }
        .animate-speak-bar {
          height: 4px;
          animation: speak-bar 0.9s ease-in-out infinite;
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}
